import { Box, Button, Flex, StarIcon, Text } from 'components'
import pluralize from 'pluralize'
import { BaseUrl, forceDark, forceLight, sendIntercomMessage } from 'utils'
import eventImg from '../../assets/event-preview.png'
import facilityImg from '../../assets/facility-preview.png'
import facility2Img from '../../assets/facility-preview2.png'

export interface Props {
  /** default one */
  variant?: 'one' | 'two'
  resourceName: 'facility' | 'event' | 'developer'
  onDismiss?: () => void
}

export default function ResourceUpsell({ variant = 'one', resourceName, onDismiss }: Props) {
  if (!resourceName) return null

  let title: string
  let description: string
  let imageSrc: string

  switch (resourceName) {
    case 'event':
      title = variant === 'one' ? 'Events' : 'Event Management'
      description =
        'The platform features real-time attendee tracking and engagement metrics, along with event-specific workflows and communication tools, facilitating better planning and execution of events and improving attendee satisfaction through more engaging experiences.'
      imageSrc = eventImg
      break
    case 'developer':
      title = variant === 'one' ? 'Developer Tools' : 'API Access'
      description =
        'Access powerful APIs and developer tools to integrate Momentify with your existing systems. Build custom integrations, automate workflows, and create seamless experiences for your users with our comprehensive developer platform.'
      imageSrc = variant === 'one' ? facilityImg : facility2Img
      break
    case 'facility':
      title = variant === 'one' ? 'Facilities' : 'Custom Facility Solutions'
      description =
        'Momentify integrates seamlessly with facility management systems and offers customizable interfaces for different types of facilities, streamlining operations, and enhancing visitor and employee experiences through optimized facility usage.'
      imageSrc = variant === 'one' ? facilityImg : facility2Img
      break
  }

  return (
    <Box
      {...(variant === 'one' ? forceDark : forceLight)}
      position="relative"
      overflow="hidden"
      borderRadius={variant === 'one' ? 'md' : 'xl'}
      display="flex"
      p={variant === 'one' ? 0 : 6}
      height={{ base: 'auto', lg: '250px' }}
      backgroundColor={variant === 'two' ? '#FFFFFF99' : undefined}
      flexDirection={{ base: 'column', lg: 'row' }}
    >
      <Box
        flexShrink={0}
        width={imgWidth}
        height={{ base: '200px', lg: '100%' }}
        backgroundImage={`url(${imageSrc})`}
        backgroundSize="cover"
        borderRadius={variant === 'two' ? 'md' : undefined}
        backgroundPosition="center"
      />

      {variant === 'one' && (
        <Box
          position="absolute"
          display={{ base: 'none', lg: 'block' }}
          top={0}
          bottom={0}
          width={imgWidth}
          left={0}
          backgroundImage="linear-gradient(to right, transparent 50%, #1F2B64 100%)"
        />
      )}

      <Flex
        direction="column"
        justify="center"
        p={{ base: 6, md: 10 }}
        flex={1}
        gap={2}
        {...(variant === 'one' ? { bgGradient: 'to-l', gradientFrom: '#1F2B64', gradientTo: '#3D4279' } : {})}
      >
        <Text textStyle="h4">{title}</Text>
        <Text textStyle="bodySmall" opacity={0.6}>
          {description}
        </Text>
        <Flex mt={4} gap={4} flexWrap="wrap">
          <Button
            size="lg"
            onClick={() => window.open(`${BaseUrl.root}/#whymomentify-section`, '_blank')}
            variant="solid"
          >
            Learn More
          </Button>
          <Button
            size="lg"
            display={{ base: 'flex', lg: 'none', xl: 'flex' }}
            palette="secondary"
            leftIcon={<StarIcon />}
            onClick={() =>
              sendIntercomMessage(
                `Hello, I would like to add ${pluralize(resourceName)} to my current subscription`
              )
            }
            variant="solid"
          >
            Unlock
          </Button>
          {onDismiss && (
            <Button size="lg" background="transparent" variant="outline" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

const imgWidth = { base: '100%', lg: 300, xl: 400 }
